/*
  Contact popups ("Your Message"): Form Agency and Form Generic (issue #95).
  Extends the theme's ModalElement (assets/theme.js): any element with
  aria-controls="<popup id>" opens it, Escape closes it, focus is trapped.

  Opening from a navigation menu: menu links cannot carry aria-controls, so a link whose
  URL ends in "#<popup id>" (e.g. "#ContactPopupAgency") opens the popup too, and the popup
  opens on page load when the page address has that hash.

  Submission: multipart POST to the section's endpoint constant (Mailjet via a Cloudflare
  Worker, na-web-endpoint/contact-form). With no endpoint configured it falls back to
  Shopify's native contact form.
*/

// Field rules. Keep in sync with the endpoint (na-web-endpoint/contact-form/handler.mjs).
// Names: letters only (any language) plus space, hyphen, apostrophe and dot.
var CP_NAME = /^[\p{L}\p{M}][\p{L}\p{M}' .\-’]*$/u;
// Phone: digits with the usual separators ( ) - / . and spaces, optionally starting with +.
var CP_PHONE = /^\+?[0-9 ()\/.\-]*[0-9][0-9 ()\/.\-]*$/;
// Email: needs a real ending of two or more letters (.de, .com, ...).
var CP_EMAIL = /^[^\s@]+@([\p{L}\p{N}-]+\.)+\p{L}{2,}$/u;
var CP_RULES = { name: CP_NAME, phone: CP_PHONE, email: CP_EMAIL };
// Texts come from the section (data-msg-*, English or German); these are the fallbacks.
var CP_MESSAGES = {
  required: 'This field is required',
  name: 'Please use letters only.',
  phone: 'Please enter a valid phone number.',
  email: 'Please enter a valid email address.',
  captcha: 'The security check failed. Please reload the page and try again.',
};
// Country list (ISO 3166-1 alpha-2, without uninhabited territories). The option value is the English
// name (what the team reads in the email), the visible text is in the page language.
var CP_COUNTRIES = 'AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(' ');

// Cloudflare Turnstile (bot check): the script is loaded once, the first time a popup opens.
function cpLoadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  if (!window.__cpTurnstile) {
    window.__cpTurnstile = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        window.__cpTurnstile = null;
        reject(new Error('Turnstile script could not be loaded'));
      };
      document.head.append(script);
    });
  }

  return window.__cpTurnstile;
}

// `var` + class expression (not a class declaration) so re-running this script (two popups on one
// page load it twice, and the theme editor re-runs it) doesn't throw "already been declared".
var ContactPopupModal = class ContactPopupModal extends ModalElement {
  get shouldLock() {
    return true;
  }

  // First control of the screen that is showing (the success step can be the first one after a page reload).
  get focusElement() {
    return this.focusableIn(this.activeContainer)[0] || this.querySelector('[data-close]') || this.querySelector('button');
  }

  connectedCallback() {
    super.connectedCallback();

    this.form = this.querySelector('.contact-popup__form');
    if (!this.form) return;

    this.resetOnClose = false;

    this.alertEl = this.querySelector('[data-form-alert]');
    this.submitButton = this.querySelector('[data-submit]');
    this.pageUrlField = this.querySelector('[data-page-url-field]');

    this.endpoint = (this.dataset.endpoint || '').trim();
    this.turnstileKey = (this.dataset.turnstileSitekey || '').trim();
    this.turnstileBox = this.querySelector('[data-turnstile]');
    this.turnstileWidget = null;
    this.turnstileLoading = false;

    this.messages = {
      required: this.dataset.msgRequired || CP_MESSAGES.required,
      name: this.dataset.msgName || CP_MESSAGES.name,
      phone: this.dataset.msgPhone || CP_MESSAGES.phone,
      email: this.dataset.msgEmail || CP_MESSAGES.email,
      captcha: this.dataset.msgCaptcha || CP_MESSAGES.captcha,
    };

    const signal = { signal: this.abortController.signal };

    this.querySelectorAll('[data-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.hide();
      }, signal);
    });

    // "Leave form" confirmation (shown by hide() when something was entered).
    this.querySelector('[data-leave-form]')?.addEventListener('click', () => this.leaveForm(), signal);
    this.querySelectorAll('[data-cancel-leave]').forEach((button) => {
      button.addEventListener('click', () => this.cancelLeave(), signal);
    });

    // Our own focus trap (the theme's only wraps between the first and last focusable element of the
    // whole modal, which includes hidden steps, so Tab could leave the dialog or the confirmation).
    document.addEventListener('keydown', this.onKeydown.bind(this), { capture: true, signal: this.abortController.signal });
    document.addEventListener('focusin', this.onFocusIn.bind(this), { capture: true, signal: this.abortController.signal });
    this.syncInert();

    // Capture phase on window: runs before the theme's hover-button and Shopify's spam-protection
    // script (both listen on the form itself), so an invalid or endpoint submit can be stopped
    // before either of them reacts to it.
    window.addEventListener('submit', this.onSubmit.bind(this), { capture: true, signal: this.abortController.signal });
    this.form.addEventListener('input', this.onFieldInput.bind(this), signal);
    this.form.addEventListener('change', this.onFieldInput.bind(this), signal);

    // Links that open the popup ("#<popup id>" and the configured footer links).
    // Capture phase, so a link's own handlers (or the theme's) can't navigate away first.
    document.addEventListener('click', this.onHashLinkClick.bind(this), { capture: true, signal: this.abortController.signal });

    this.populateCountries();
    this.markTriggerLinks();
    this.syncSelectStates();

    // Native contact-form fallback: Shopify reloads the page after the post.
    if (this.querySelector('[data-form-posted-successfully]')) {
      this.setStep('success');
      this.show(null, false);
    } else {
      const postedError = this.querySelector('[data-form-posted-error]');
      if (postedError) {
        this.showAlert(postedError.textContent.trim() || this.dataset.errorText);
        this.show(null, false);
      } else if (this.hashMatches(window.location.hash)) {
        // Deep link, e.g. from a menu on another page or an email.
        const open = () => this.show(null, false);
        if (document.readyState === 'complete') open();
        else window.addEventListener('load', open, { once: true, signal: this.abortController.signal });
      }
    }
  }

  /* Opening ------------------------------------------------------- */

  // "host/path" of an address, without language prefix, query, hash, "www." or a trailing slash.
  // `base` resolves relative addresses (the page itself for links, the shop for configured paths).
  static addressKey(address, base = window.location.href) {
    try {
      const url = new URL(address, base);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let path = url.pathname;

      // /en/pages/contact -> /pages/contact (only for addresses on this shop).
      if (url.host === window.location.host && root !== '/' && path.toLowerCase().startsWith(root.toLowerCase())) {
        path = `/${path.slice(root.length)}`;
      }

      return `${url.host.replace(/^www\./i, '')}${path.replace(/\/+$/, '')}`.toLowerCase();
    } catch (error) {
      return '';
    }
  }

  // The footer addresses that open this popup (data-trigger-urls), as keys.
  get triggerKeys() {
    if (!this._triggerKeys) {
      this._triggerKeys = (this.dataset.triggerUrls || '')
        .split(/[\s,]+/)
        .filter(Boolean)
        .map((address) => ContactPopupModal.addressKey(address, window.location.origin))
        .filter(Boolean);
    }

    return this._triggerKeys;
  }

  // Three kinds of elements open the popup:
  // 1. "#<popup id>" links on this page,
  // 2. footer menu links whose address is listed in the section's trigger URLs, and
  // 3. elements with data-contact-popup="<popup id>" (the footer menu item whose URL is just "#", for example
  //    "Contact" in the SHOPS & RETAILERS list). Such a trigger inside an accordion <summary> keeps toggling it
  //    on a phone.
  // Anything else, and modified clicks (new tab, new window), behave as usual.
  onHashLinkClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-contact-popup]');
    if (trigger && trigger.dataset.contactPopup === this.id) {
      event.preventDefault();

      const summary = trigger.closest('summary');
      if (summary && window.matchMedia('(max-width: 768px)').matches) {
        summary.parentElement.open = !summary.parentElement.open;
      } else if (!this.open) {
        this.show(trigger);
      }
      return;
    }

    const link = event.target.closest('a[href]');
    if (!link || !this.matchesLink(link)) return;

    event.preventDefault();
    if (!this.open) this.show(link);
  }

  // Is this "#...": the id of this popup? The case does not matter, so a hand-typed "#contactpopupagency" works.
  hashMatches(hash) {
    try {
      return decodeURIComponent(hash || '').replace(/^#/, '').toLowerCase() === this.id.toLowerCase();
    } catch (error) {
      return false;
    }
  }

  matchesLink(link) {
    if (this.hashMatches(link.hash)) {
      // Only links that point at this very page (a trailing slash, "www." or the language prefix may differ);
      // links to another page navigate there and open the popup through the deep link.
      return ContactPopupModal.addressKey(link.href) === ContactPopupModal.addressKey(window.location.href);
    }

    if (!this.triggerKeys.length || !link.closest('footer, .shopify-section-group-footer-group')) return false;

    return this.triggerKeys.includes(ContactPopupModal.addressKey(link.href));
  }

  show(activeElement = null, animate = true) {
    if (this.pageUrlField) this.pageUrlField.value = window.location.href.split('#')[0];

    const shown = super.show(activeElement, animate);
    this.prepareTurnstile();

    return shown;
  }

  // Links that open this popup (a button's link set to "#<popup id>" in the theme editor) are announced as
  // opening a dialog.
  markTriggerLinks() {
    document.querySelectorAll('a[href*="#"]').forEach((link) => {
      if (this.hashMatches(link.hash) && this.matchesLink(link)) link.setAttribute('aria-haspopup', 'dialog');
    });
  }

  // Fills the country select (once). Without Intl.DisplayNames the codes themselves are used.
  populateCountries() {
    const select = this.querySelector('[data-country-select]');
    if (!select || select.options.length > 1) return;

    const language = document.documentElement.lang || 'en';
    let valueNames = null;
    let labelNames = null;

    try {
      valueNames = new Intl.DisplayNames(['en'], { type: 'region' });
      labelNames = new Intl.DisplayNames([language, 'en'], { type: 'region' });
    } catch (error) {
      // Older browser: fall back to the codes.
    }

    const nameOf = (names, code) => {
      try {
        return (names && names.of(code)) || code;
      } catch (error) {
        return code;
      }
    };

    const options = CP_COUNTRIES.map((code) => ({ value: nameOf(valueNames, code), label: nameOf(labelNames, code) }));
    options.sort((a, b) => a.label.localeCompare(b.label, language));

    const fragment = document.createDocumentFragment();
    options.forEach(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      fragment.append(option);
    });

    select.append(fragment);
  }

  syncSelectStates() {
    this.querySelectorAll('select').forEach((select) => {
      select.closest('.field')?.classList.toggle('has-value', select.value !== '');
    });
  }

  setStep(step) {
    this.setAttribute('data-step', step);
    this.syncInert();

    // The form step just became inert: move focus to the success step's button.
    if (step === 'success') this.querySelector('.contact-popup__step--success [data-close]')?.focus();
  }

  /* Focus --------------------------------------------------------- */

  // The screen that is currently active: only its controls may be reached by keyboard.
  get stepContainers() {
    return {
      form: this.querySelector('.contact-popup__step--form'),
      'leave-confirm': this.querySelector('.contact-popup__leave-dialog'),
      success: this.querySelector('.contact-popup__step--success'),
    };
  }

  get activeContainer() {
    return this.stepContainers[this.getAttribute('data-step')] || null;
  }

  // Everything outside the active screen is made unavailable to the keyboard and to screen readers.
  syncInert() {
    const step = this.getAttribute('data-step');
    this.stepContainers.form?.toggleAttribute('inert', step !== 'form');
    this.querySelector('.contact-popup__leave-overlay')?.toggleAttribute('inert', step !== 'leave-confirm');
    this.stepContainers.success?.toggleAttribute('inert', step !== 'success');
  }

  // Visible and available inside `scope` (a hidden or inert ancestor *within* the scope counts; the popup's
  // own state does not).
  isVisible(el, scope = this) {
    const blocked = el.closest('[hidden], [inert]');
    if (blocked && scope.contains(blocked)) return false;
    if (typeof el.checkVisibility === 'function') return el.checkVisibility({ visibilityProperty: true });
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  focusableIn(container) {
    if (!container) return [];

    // iframe: the Turnstile widget, only reachable while it is showing (it has no size otherwise).
    const selector = 'a[href], button, input:not([type="hidden"]), select, textarea, iframe, [tabindex]';
    return Array.from(container.querySelectorAll(selector)).filter(
      (el) => !el.disabled && el.tabIndex >= 0 && this.isVisible(el, container) && (el.tagName !== 'IFRAME' || el.offsetHeight > 0)
    );
  }

  onKeydown(event) {
    if (event.key !== 'Tab' || !this.open) return;

    const container = this.activeContainer;
    const items = this.focusableIn(container);
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (!first) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    let target = null;
    if (!container.contains(active)) target = event.shiftKey ? last : first;
    else if (event.shiftKey && active === first) target = last;
    else if (!event.shiftKey && active === last) target = first;

    if (target) {
      event.preventDefault();
      event.stopPropagation(); // the theme's own trap must not act on this Tab as well
      target.focus();
    }
  }

  // Safety net: if focus lands outside the active screen while the popup is open, bring it back.
  onFocusIn(event) {
    if (!this.open) return;

    const container = this.activeContainer;
    if (!container || container.contains(event.target)) return;

    this.focusableIn(container)[0]?.focus();
  }

  /* Bot check (Cloudflare Turnstile) ------------------------------- */

  get usesTurnstile() {
    return !!(this.endpoint && this.turnstileKey && this.turnstileBox);
  }

  // Renders the widget (once). It runs in the background and hands out a one-time token per message.
  prepareTurnstile() {
    if (!this.usesTurnstile || this.turnstileWidget !== null || this.turnstileLoading) return Promise.resolve();

    this.turnstileLoading = true;

    return cpLoadTurnstile()
      .then((turnstile) => {
        this.turnstileWidget = turnstile.render(this.turnstileBox, {
          sitekey: this.turnstileKey,
          language: 'auto',
          theme: 'light',
          size: 'flexible',
          appearance: 'interaction-only',
          'refresh-expired': 'auto',
          // Only visible when Cloudflare needs the visitor to do something.
          'before-interactive-callback': () => this.turnstileBox.classList.add('is-interactive'),
          'after-interactive-callback': () => this.turnstileBox.classList.remove('is-interactive'),
        });
      })
      .catch(() => {})
      .finally(() => {
        this.turnstileLoading = false;
      });
  }

  // Resolves with the token as soon as the widget has one, or '' after the timeout.
  waitForTurnstileToken(timeout = 15000) {
    const started = Date.now();

    return this.prepareTurnstile().then(
      () =>
        new Promise((resolve) => {
          const poll = () => {
            const token = this.turnstileWidget !== null && window.turnstile ? window.turnstile.getResponse(this.turnstileWidget) : '';
            if (token) return resolve(token);
            if (Date.now() - started > timeout) return resolve('');
            setTimeout(poll, 200);
          };
          poll();
        })
    );
  }

  // A token works once; ask for a fresh one after every attempt.
  resetTurnstile() {
    if (this.turnstileWidget !== null && window.turnstile) window.turnstile.reset(this.turnstileWidget);
  }

  /* Closing ------------------------------------------------------- */

  // Anything the visitor typed or picked.
  isDirty() {
    return Array.from(this.form.elements).some((el) => {
      if (!el.name || el.type === 'hidden' || el.name === 'website') return false;
      if (el.type === 'checkbox' || el.type === 'radio') return el.checked;
      return typeof el.value === 'string' && el.value.trim() !== '';
    });
  }

  // Every close path (X, Escape, "continue") comes through here. Closing a filled-in form asks first.
  hide(force = false) {
    if (!force && this.open) {
      const step = this.getAttribute('data-step');

      if (step === 'leave-confirm') {
        this.cancelLeave();
        return;
      }

      if (step === 'form' && this.isDirty()) {
        this.focusBeforeLeave = document.activeElement;
        this.setStep('leave-confirm');
        this.querySelector('.contact-popup__leave-actions [data-cancel-leave]')?.focus();
        return;
      }
    }

    return super.hide();
  }

  cancelLeave() {
    this.setStep('form');

    // Back to where the visitor was (a field, say); fall back to the close button.
    const before = this.focusBeforeLeave;
    this.focusBeforeLeave = null;

    if (before && before.isConnected && this.stepContainers.form.contains(before) && this.isVisible(before, this.stepContainers.form)) {
      before.focus();
    } else {
      this.querySelector('.contact-popup__step--form [data-close]')?.focus();
    }
  }

  leaveForm() {
    this.resetOnClose = true;
    this.hide(true);
  }

  afterHide() {
    super.afterHide();

    if (this.resetOnClose) {
      setTimeout(() => this.resetForm(), 300);
    }
  }

  resetForm() {
    this.resetOnClose = false;
    this.form.reset();
    this.clearAlert();
    this.form.querySelectorAll('.has-error').forEach((el) => el.classList.remove('has-error'));
    this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    this.syncSelectStates();
    this.setStep('form');
  }

  /* Validation ---------------------------------------------------- */

  errorHost(el) {
    if (el.type === 'checkbox') return this.querySelector('.contact-popup__consent-wrap');
    return el.closest('.field');
  }

  errorTarget(el) {
    if (el.type === 'checkbox') return this.querySelector('.contact-popup__consent');
    return null;
  }

  setError(el, message) {
    const host = this.errorHost(el);
    if (!host) return;

    host.classList.add('has-error');
    this.errorTarget(el)?.classList.add('has-error');
    el.setAttribute('aria-invalid', 'true');

    const text = host.querySelector(':scope > .field__error');
    if (text && message) text.textContent = message;
  }

  clearError(host) {
    if (!host) return;

    host.classList.remove('has-error');
    host.querySelector('.contact-popup__consent')?.classList.remove('has-error');
    host.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
  }

  // '' when the field is fine, otherwise the error text.
  fieldError(el) {
    if (el.type === 'checkbox') return el.checked || !el.required ? '' : this.messages.required;

    const value = el.value.trim();
    if (!value) return el.required ? this.messages.required : '';

    const rule = CP_RULES[el.dataset.rule];
    if (rule && !rule.test(value)) return this.messages[el.dataset.rule];

    return '';
  }

  validate() {
    let firstInvalid = null;

    this.form.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.type === 'hidden' || el.disabled || el.name === 'website') return;

      const message = this.fieldError(el);
      if (!message) {
        this.clearError(this.errorHost(el));
        return;
      }

      this.setError(el, message);
      if (!firstInvalid) firstInvalid = el;
    });

    firstInvalid?.focus();

    return !firstInvalid;
  }

  onFieldInput(event) {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;

    if (el.tagName === 'SELECT') this.syncSelectStates();

    if (!this.fieldError(el)) {
      this.clearError(this.errorHost(el));
    }
  }

  /* Submit -------------------------------------------------------- */

  showAlert(message) {
    this.alertEl.textContent = message;
    this.alertEl.hidden = false;
  }

  clearAlert() {
    this.alertEl.textContent = '';
    this.alertEl.hidden = true;
  }

  setBusy(busy) {
    if (!this.submitButton) return;

    this.submitButton.toggleAttribute('disabled', busy);
    if (busy) {
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  // Endpoint mode: multipart POST, answered by the endpoint (needs CORS).
  buildRequest(captchaToken = '') {
    const data = new FormData(this.form);
    if (captchaToken) data.append('cf-turnstile-response', captchaToken);

    return fetch(this.endpoint, { method: 'POST', body: data });
  }

  onSubmit(event) {
    if (event.target !== this.form) return;

    this.clearAlert();
    this.syncSelectStates();

    if (!this.validate()) {
      // Stop it here: otherwise the theme marks the button busy and Shopify's captcha
      // script would still post the incomplete form natively.
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // No endpoint: let the browser post Shopify's native contact form (full page reload; the
    // popup reopens on its success step).
    if (!this.endpoint) {
      this.setBusy(true);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setBusy(true);

    const captcha = this.usesTurnstile ? this.waitForTurnstileToken() : Promise.resolve('');

    captcha
      .then((token) => {
        if (this.usesTurnstile && !token) throw Object.assign(new Error('No bot-check token'), { captcha: true });

        return this.buildRequest(token);
      })
      .then((response) => {
        // 403 / 503: the bot check was refused or Cloudflare could not be asked.
        if (this.usesTurnstile && (response.status === 403 || response.status === 503)) throw Object.assign(new Error('Bot check refused'), { captcha: true });
        if (!response.ok) throw new Error(`Contact form submission failed: ${response.status}`);

        this.resetOnClose = true;
        this.setStep('success');
      })
      .catch((error) => {
        this.showAlert(error && error.captcha ? this.messages.captcha : this.dataset.errorText || 'Sending failed. Please try again later.');
      })
      .finally(() => {
        this.resetTurnstile();
        this.setBusy(false);
      });
  }
};

// The theme editor re-runs section scripts when the section re-renders.
if (!customElements.get('contact-popup-modal')) {
  customElements.define('contact-popup-modal', ContactPopupModal);
}
